import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { getNotifications, markNotificationAsRead } from '../services/supabaseService';
import { formatDistanceToNow } from 'date-fns';

const NotificationPanel = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && user) {
      loadNotifications();
    }
  }, [isOpen, user]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await getNotifications(user?.id, 20);
      setNotifications(data || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev?.map(n => (n?.id === notificationId ? { ...n, isRead: true } : n))
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'spending_threshold':
        return { name: 'AlertTriangle', color: 'var(--color-warning)' };
      case 'budget_limit':
        return { name: 'AlertCircle', color: 'var(--color-error)' };
      case 'goal_progress':
        return { name: 'Target', color: 'var(--color-success)' };
      case 'bill_reminder':
        return { name: 'Calendar', color: 'var(--color-accent)' };
      case 'weekly_digest':
        return { name: 'TrendingUp', color: 'var(--color-primary)' };
      default:
        return { name: 'Bell', color: 'var(--color-muted-foreground)' };
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full md:w-96 bg-card border-l shadow-elevation-3 z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={20} color="var(--color-primary)" strokeWidth={2} />
            <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
            {notifications?.filter(n => !n?.isRead)?.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary text-primary-foreground">
                {notifications?.filter(n => !n?.isRead)?.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" strokeWidth={2} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-muted-foreground">Loading...</p>
              </div>
            </div>
          ) : notifications?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Icon name="Bell" size={32} color="var(--color-muted-foreground)" strokeWidth={2} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No notifications</h3>
              <p className="text-sm text-muted-foreground">
                You're all caught up! We'll notify you about important updates.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications?.map((notification) => {
                const icon = getNotificationIcon(notification?.notificationType);
                return (
                  <div
                    key={notification?.id}
                    className={`p-4 hover:bg-muted/50 transition-colors cursor-pointer ${
                      !notification?.isRead ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => !notification?.isRead && handleMarkAsRead(notification?.id)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <Icon name={icon?.name} size={20} color={icon?.color} strokeWidth={2} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-foreground">
                            {notification?.title}
                          </h4>
                          {!notification?.isRead && (
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {notification?.message}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification?.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications?.length > 0 && (
          <div className="p-4 border-t">
            <Button
              variant="ghost"
              fullWidth
              size="sm"
              onClick={loadNotifications}
              iconName="RefreshCw"
            >
              Refresh
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationPanel;